import type { ReactNode } from 'react';

interface CardProps {
  title?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Card({ title, trailing, children, className = '' }: CardProps) {
  return (
    <section
      className={`rounded-lg border bg-[color:var(--bg-primary)] ${className}`}
      style={{ borderColor: 'var(--border)' }}
    >
      {(title || trailing) && (
        <header
          className="flex items-center justify-between gap-3 px-5 py-3 border-b"
          style={{ borderColor: 'var(--border)' }}
        >
          <h2 className="text-[15px] font-medium">{title}</h2>
          {trailing}
        </header>
      )}
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

export function Metric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div
      className="rounded-md px-4 py-3"
      style={{ background: 'var(--bg-secondary)' }}
      aria-label={label}
    >
      <div className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </div>
      <div className="text-[24px] font-medium mt-1">{value}</div>
    </div>
  );
}

export type PillTone = 'success' | 'warning' | 'danger' | 'muted';

export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  const styles: Record<PillTone, { bg: string; text: string }> = {
    success: { bg: 'var(--success-bg)', text: 'var(--success-text)' },
    warning: { bg: 'var(--warning-bg)', text: 'var(--warning-text)' },
    danger: { bg: 'var(--danger-bg)', text: 'var(--danger-text)' },
    muted: { bg: 'var(--accent-muted)', text: 'var(--accent)' },
  };
  const s = styles[tone];
  return (
    <span
      className="inline-block text-[12px] font-medium px-2 py-0.5 rounded-md"
      style={{ background: s.bg, color: s.text }}
    >
      {children}
    </span>
  );
}

export function Button({
  variant = 'outline',
  size = 'sm',
  children,
  onClick,
  disabled,
  ariaLabel,
}: {
  variant?: 'outline' | 'solid';
  size?: 'sm' | 'md';
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const sizeCls = size === 'sm' ? 'text-[12px] px-2.5 py-1' : 'text-[13px] px-3 py-1.5';
  const variantStyle =
    variant === 'solid'
      ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' }
      : { background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)' };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed ${sizeCls}`}
      style={variantStyle}
    >
      {children}
    </button>
  );
}
